import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcE = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990/990EZ Schedule E: Simplifying Reporting on Schools"
          description="Simplify your tax reporting with Form 990/990EZ Schedule E. Learn the intricacies of reporting private schools for seamless compliance."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-e/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule E</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report School Information with TaxZerone
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-e/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule E at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Purpose: Form 990 Schedule E{' '}
                        </strong>{' '}
                        is used by organizations operating{' '}
                        <strong style={{ fontWeight: 600 }}>schools</strong> to
                        report compliance with the IRS{' '}
                        <strong style={{ fontWeight: 600 }}>
                          racial nondiscrimination requirements{' '}
                        </strong>
                        for students and other required school-related
                        disclosures.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Must File?
                        </strong>{' '}
                        Organizations operating a{' '}
                        <strong style={{ fontWeight: 600 }}>school</strong> and
                        filing{' '}
                        <strong style={{ fontWeight: 600 }}>Form 990</strong> or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-EZ{' '}
                        </strong>
                        generally must attach{' '}
                        <strong style={{ fontWeight: 600 }}>Schedule E</strong>{' '}
                        to meet the IRS school reporting requirements.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Reported?
                        </strong>{' '}
                        Schedule E reports the school's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          racial nondiscrimination policy,
                        </strong>{' '}
                        public notice requirements, student admissions,
                        scholarships, and other required compliance information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Is Considered a School?
                        </strong>{' '}
                        For Schedule E, a school is an organization that
                        provides a{' '}
                        <strong style={{ fontWeight: 600 }}>
                          regular curriculum, qualified instructors,
                        </strong>{' '}
                        and an{' '}
                        <strong style={{ fontWeight: 600 }}>
                          enrolled student body{' '}
                        </strong>
                        at a regular educational location.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule E?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule E?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#sche" sx={{ color: '#0202ff' }}>
                          Schedule E Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule E filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule E?
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  <strong style={{ fontWeight: 600 }}>Schedule E, </strong>
                  <i> Schools </i> is a supplementary schedule filed annually by
                  school organizations along with their Form 990 or{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-ez-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>{' '}
                  return to report additional information and meet their{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    nonprofit tax filing
                  </Link>{' '}
                  requirements completely.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule E?
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Organizations that are organized and operate as{' '}
                  <strong style={{ fontWeight: 600 }}>Private Schools</strong>{' '}
                  described in section 170(b)(1)(A)(ii) are required to attach
                  Schedule E along with their Form 990/990-EZ information
                  return.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="sche"
                >
                  Schedule E Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-sche-org-img.webp" />
                  <source srcSet="/assets/images/f990-sche-org-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-sche-org-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule E showing organization details and basic information"
                    width={760}
                    height={136}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  All non-profits and{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    exempt organizations
                  </Link>{' '}
                  i.e., schools that file Form 990/990-EZ must complete and
                  attach Schedule E.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule E filing requirements for
                  each part.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-sche-part1-img.webp" />
                  <source srcSet="/assets/images/f990-sche-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-sche-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule E Part I racial nondiscrimination policy reporting for educational organizations"
                    width={760}
                    height={285}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if your organization has clearly written down and
                  officially approved a policy that students are accepted and
                  treated equally, without discrimination based on race. This
                  statement should be included in your governing documents, such
                  as your charter, bylaws, or a resolution approved by your
                  board.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer "Yes" if you actually share that policy with the
                  public. It needs to be easy to find in your brochures,
                  admissions info, or scholarship announcements. In short: the
                  policy needs to exist on paper and be visible to everyone you
                  serve.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 3
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Answer <span style={{ fontWeight: 600 }}> “Yes”</span> if
                    your organization has clearly made its racially
                    nondiscriminatory policy visible to the public during the
                    year.
                  </li>
                  <li>
                    This can be done by displaying the notice on your publicly
                    accessible website homepage, publishing it in newspapers, or
                    sharing it through broadcast media during student admissions
                    or registration periods, so the community is aware that the
                    school treats all students equally.{' '}
                  </li>
                  <li>
                    If you select{' '}
                    <span style={{ fontWeight: 600 }}>“Yes,”</span> briefly
                    explain how the policy was shared. If{' '}
                    <span style={{ fontWeight: 600 }}>“No,”</span> explain why
                    it wasn’t publicized. Use{' '}
                    <span style={{ fontWeight: 600 }}>Part II</span> if you need
                    extra space.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f990-sche-part1-line4-img.webp" />
                  <source srcSet="/assets/images/f990-sche-part1-line4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-sche-part1-line4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule E, Part I, questions 4–5 on required records and racial nondiscrimination in admissions, staffing, scholarships, and programs."
                    width={760}
                    height={460}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These questions ask whether your organization keeps proper
                  records to show that it follows fair and nondiscriminatory
                  practices.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>“Yes”</span> if you
                  keep records showing the racial makeup of your students,
                  faculty, and administrative staff.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>“Yes”</span> if you
                  maintain documents showing that scholarships or financial
                  assistance are awarded fairly, without discrimination based on
                  race.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>“Yes”</span> if you
                  keep copies of brochures, catalogs, announcements, or other
                  written materials shared with the public about admissions,
                  programs, or scholarships.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 4d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer <span style={{ fontWeight: 600 }}>“Yes”</span> if you
                  keep copies of all fundraising or donation request materials
                  used by your organization or prepared on its behalf. If you
                  answer <span style={{ fontWeight: 600 }}>“No”</span> to any
                  question, briefly explain the reason. Use{' '}
                  <span style={{ fontWeight: 600 }}>Part II</span> if more space
                  is needed.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This line asks if your school treats anyone unfairly based on
                  race in any part of its operations. Go through each item and
                  answer “Yes” only if there is a problem; otherwise, answer
                  “No.”
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “No” if all students receive the same rights, services,
                  and opportunities, and race does not affect how they
                  participate or benefit from school activities. Select “Yes”
                  only if race changes or limits the privileges given to any
                  student.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “No” if your school accepts students fairly and gives
                  everyone an equal chance, no matter their race. Answer “Yes”
                  only if a student race is taken into account when deciding who
                  is accepted for admission.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “No” if hiring, promotions, and other employment
                  decisions for faculty and administrative staff are handled
                  fairly and without regard to race. Select “Yes” only if race
                  affects employment decisions.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5d
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “No” if all scholarships, grants, and other financial
                  support are offered fairly to every student, regardless of
                  race. Answer “Yes” only if race affects who receives financial
                  assistance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5e
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Review whether academic programs, classroom rules, and
                  learning opportunities are applied equally to all students.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5f
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Consider whether school spaces and resources are available to
                  students equally, without racial preference or restriction.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5g
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Confirm that sports teams and athletic opportunities are open
                  to all students on the same terms.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 5h
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Answer “No” if all students can take part in clubs, events,
                  and other non-academic activities equally, without regard to
                  race. Answer “Yes” only if race affects who can participate or
                  receive these opportunities. If you answer “Yes” to any
                  question, briefly explain the situation in Part II.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-sche-part1-line6-img.webp" />
                  <source srcSet="/assets/images/f990-sche-part1-line6-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-sche-part1-line6-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule E, Part I, questions 6–7 regarding government financial aid and certification of racial nondiscrimination compliance."
                    width={760}
                    height={99}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Answer “Yes” if your organization receives any grants,
                  funding, or other financial support from a government agency.
                  Select “No” if it does not.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 6b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  State whether your organization’s eligibility to receive this
                  type of government assistance has ever been revoked or
                  suspended. If you answer “Yes” to either question, provide a
                  brief explanation in Part II.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This question asks the organization to confirm that it has
                  followed the racial nondiscrimination requirements outlined in
                  the applicable procedures issued by the Internal Revenue
                  Service (IRS). Answer “Yes” if your organization meets these
                  requirements. If you select “No,” explain the reason in Part
                  II
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Supplemental Information
                </Typography>{' '}
                <picture>
                  <source srcSet="/assets/images/f990-sche-part2-img.webp" />
                  <source srcSet="/assets/images/f990-sche-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-sche-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule E Part II Supplemental Information"
                    width={760}
                    height={372}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part II to provide extra details or explanations when more
                  information is needed for certain answers in Part I, such as
                  lines 3, 4d, 5h, 6b, and 7. You may also use this section to
                  clarify or add more information to any other response on
                  Schedule E.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  For each explanation, clearly mention the Part and Line
                  number, and list them in the same order as they appear on the
                  form. Add extra pages if you need more space. Use Part II to
                  give additional explanations or details when required for
                  certain answers in Part I, including lines 3, 4d, 5h, 6b, and
                  7. You may also use this section to clarify or add more
                  information to any other response on Schedule E.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule E filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  As an esteemed IRS-authorized e-file service provider, we at{' '}
                  <strong style={{ fontWeight: 600 }}>
                    <i>TaxZerone</i>
                  </strong>{' '}
                  promise more than just updates – we promise instant insights
                  into your annual information return filing status, putting you
                  in control at every turn.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  At TaxZerone, it&apos;s not just about filing; it&apos;s about
                  a seamless e-filing experience crafted just for you!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>
                  /
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>{' '}
                  return with Schedule E attachment is transmitted to the IRS in
                  3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Provide Organization Details -
                    </strong>{' '}
                    Choose the tax year for which you want to file a return, and
                    provide your organization&apos;s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Preview the Return -
                    </strong>{' '}
                    Complete Schedule E and preview the information provided in
                    the return for accuracy before transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Transmit to the IRS -
                    </strong>{' '}
                    Transmit Schedule E along with your 990/990-EZ return to the
                    IRS and get the acceptance in just a few hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your exempt return due to any reason,
                  you can correct and retransmit it to the IRS for{' '}
                  <span style={{ fontWeight: 600 }}>FREE!</span> With TaxZerone,
                  you&apos;re not alone – we&apos;re your partners in success.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'column' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        <strong style={{ fontWeight: 600 }}>
                          Embark on a stress-free tax journey with TaxZerone –
                        </strong>
                        your gateway to effortless filing!
                      </Typography>
                      <Typography variant="body2">
                        Your journey to seamless tax compliance begins here!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 300, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990sche`}
                      >
                        E-file Your Form 990/990-EZ
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990se.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule E (Form 990 or 990-EZ) – Schools
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the purpose of Schedule E?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" sx={{ mt: 0.5 }} itemProp="text">
                    The purpose of filing Schedule E is to provide more detailed
                    information on private schools.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What kind of information is reported in Schedule E?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 1 }}>
                      Schedule E requires schools to answer questions regarding
                      their racial non-discrimination policy, the methods used
                      to communicate the policy, and details about admissions,
                      scholarships, and other policies to ensure compliance.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Is there a filing deadline for Schedule E?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mb: 2 }}>
                      There&apos;s no separate filing deadline for Schedule E as
                      it aligns with the deadline for the standard 990/990-EZ
                      return.
                    </Typography>
                    <Typography>
                      In most cases, the{' '}
                      <Link
                        href="/resources/tax-exempt/form-990-due-dates/"
                        sx={{ color: '#0202ff' }}
                      >
                        due date
                      </Link>{' '}
                      to file a 990/990-EZ return is on the{' '}
                      <strong style={{ fontWeight: 600 }}>
                        15th day of the 5th month
                      </strong>{' '}
                      after the end of your organization&apos;s fiscal year. For
                      organizations that follow the calendar year (ending on
                      December 31), the due date is{' '}
                      <strong style={{ fontWeight: 600 }}>May 15th.</strong>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcE;
